(() => {
  const configSelector = '[data-product-3d-hotspots-config]';
  const hostSelector = '[data-product-3d-hotspots] product-model[data-product-id]';

  function readConfigs() {
    return Array.from(document.querySelectorAll(configSelector)).reduce((configs, script) => {
      try {
        configs.set(String(script.dataset.productId), JSON.parse(script.textContent));
      } catch (error) {
        console.warn('Product 3D hotspots: invalid configuration.', error);
      }
      return configs;
    }, new Map());
  }

  function getHosts(productId) {
    return Array.from(document.querySelectorAll(hostSelector)).filter(
      (host) => String(host.dataset.productId) === String(productId),
    );
  }

  function getViewer(host) {
    return host?.querySelector('model-viewer');
  }

  function numberOrZero(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function modelCoordinate(point) {
    return `${numberOrZero(point?.x)}m ${numberOrZero(point?.y)}m ${numberOrZero(point?.z)}m`;
  }

  function normalCoordinate(normal) {
    if (
      !normal ||
      ['x', 'y', 'z'].some(
        (axis) => normal[axis] === null || normal[axis] === undefined || normal[axis] === '' || !Number.isFinite(Number(normal[axis])),
      )
    ) {
      return null;
    }

    return `${normal.x}m ${normal.y}m ${normal.z}m`;
  }

  function setViewerCamera(viewer, feature) {
    // A lower interpolation decay makes camera changes feel like Apple's
    // guided product views instead of an abrupt jump.
    if ('interpolationDecay' in viewer) {
      viewer.interpolationDecay = 120;
      viewer.setAttribute('interpolation-decay', '120');
    }

    if (feature.cameraOrbit) {
      viewer.cameraOrbit = feature.cameraOrbit;
      viewer.setAttribute('camera-orbit', feature.cameraOrbit);
    }
    if (feature.cameraTarget) {
      viewer.cameraTarget = feature.cameraTarget;
      viewer.setAttribute('camera-target', feature.cameraTarget);
    }
    if (feature.fieldOfView) {
      viewer.fieldOfView = feature.fieldOfView;
      viewer.setAttribute('field-of-view', feature.fieldOfView);
    }
  }

  function setViewerDefaultCamera(viewer, feature) {
    if (!viewer || !feature) return;

    if (feature.cameraOrbit) {
      const orbit = String(feature.cameraOrbit).replace(/([-+]?\d*\.?\d+)m\s*$/, (match, distance) => {
        const widerDistance = Number(distance) * 1.35;
        return `${widerDistance.toFixed(2)}m`;
      });
      viewer.cameraOrbit = orbit;
      viewer.setAttribute('camera-orbit', orbit);
    }
    if (feature.cameraTarget) {
      viewer.cameraTarget = feature.cameraTarget;
      viewer.setAttribute('camera-target', feature.cameraTarget);
    }

    // Start wide enough to show the whole dumbbell; feature selection still
    // applies each hotspot's closer, configured field of view.
    viewer.fieldOfView = '45deg';
    viewer.setAttribute('field-of-view', '45deg');
  }

  function formatCameraValue(value) {
    return value && typeof value.toString === 'function' ? value.toString() : '';
  }

  function formatCalibrationValue(value) {
    return value && typeof value.toString === 'function' ? value.toString() : '';
  }

  function setupCalibration(section, viewer) {
    if (!section || section.dataset.product3dHotspotsCalibration !== 'true') return;
    if (viewer.dataset.product3dHotspotsCalibrationReady === 'true') return;

    viewer.dataset.product3dHotspotsCalibrationReady = 'true';
    const calibration = document.createElement('aside');
    calibration.className = 'product-3d-hotspots__calibration';
    calibration.innerHTML = `
      <strong>Hotspot calibration mode</strong>
      <p>Click a point on the model to generate values for the Shopify hotspot fields.</p>
      <pre data-product-3d-hotspots-calibration-output>Click the model to begin.</pre>
      <a href="https://modelviewer.dev/editor/" target="_blank" rel="noopener">Open the model-viewer editor</a>
      <button type="button" data-product-3d-hotspots-calibration-copy disabled>Copy values</button>
    `;

    section.querySelector('.product-3d-hotspots__viewer')?.appendChild(calibration);
    const output = calibration.querySelector('[data-product-3d-hotspots-calibration-output]');
    const copyButton = calibration.querySelector('[data-product-3d-hotspots-calibration-copy]');
    let latestValues = '';

    if (typeof viewer.positionAndNormalFromPoint !== 'function') {
      output.textContent = 'Shopify\'s viewer does not expose point-picking. Use the model-viewer editor to obtain Position and Normal values.';
    }

    viewer.addEventListener('click', (event) => {
      if (event.target.closest?.('.product-3d-hotspot')) return;
      if (typeof viewer.positionAndNormalFromPoint !== 'function') {
        output.textContent = 'Shopify\'s viewer does not expose point-picking. Use the model-viewer editor to obtain Position and Normal values.';
        return;
      }

      const hit = viewer.positionAndNormalFromPoint(event.clientX, event.clientY);
      if (!hit) {
        output.textContent = 'No model surface was found at that point.';
        copyButton.disabled = true;
        latestValues = '';
        return;
      }

      const position = formatCalibrationValue(hit.position);
      const normal = formatCalibrationValue(hit.normal);
      const cameraOrbit = formatCameraValue(viewer.cameraOrbit);
      const cameraTarget = formatCameraValue(viewer.cameraTarget);
      const fieldOfView = formatCameraValue(viewer.fieldOfView);
      latestValues = [
        `Position X: ${position.split(' ')[0] || ''}`,
        `Position Y: ${position.split(' ')[1] || ''}`,
        `Position Z: ${position.split(' ')[2] || ''}`,
        `Normal X: ${normal.split(' ')[0] || ''}`,
        `Normal Y: ${normal.split(' ')[1] || ''}`,
        `Normal Z: ${normal.split(' ')[2] || ''}`,
        `Camera Orbit: ${cameraOrbit}`,
        `Camera Target: ${cameraTarget}`,
        `Field of View: ${fieldOfView.replace(/deg$/, '')}`,
      ].join('\n');
      output.textContent = latestValues;
      copyButton.disabled = false;
    });

    copyButton.addEventListener('click', async () => {
      if (!latestValues) return;
      try {
        await navigator.clipboard.writeText(latestValues);
        copyButton.textContent = 'Copied';
        window.setTimeout(() => { copyButton.textContent = 'Copy values'; }, 1500);
      } catch (error) {
        output.focus?.();
      }
    });
  }

  function activateFeature(productId, featureId, viewer, configs, options = {}) {
    const features = configs.get(String(productId)) || [];
    const feature = features.find((item) => String(item.id) === String(featureId));
    if (!feature) return;

    const targetViewer = viewer || getHosts(productId).map(getViewer).find(Boolean);
    if (targetViewer && options.setCamera !== false) {
      setViewerCamera(targetViewer, feature);
    }

    document
      .querySelectorAll(`[data-product-3d-hotspot-trigger][data-product-id="${productId}"]`)
      .forEach((trigger) => {
        const isActive = String(trigger.dataset.hotspotId) === String(featureId);
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-expanded', String(isActive));
        trigger.closest('.product-3d-hotspots__item')?.classList.toggle('is-active', isActive);
      });

    document
      .querySelectorAll(`[data-product-3d-hotspot-panel][data-product-id="${productId}"]`)
      .forEach((panel) => {
        panel.hidden = String(panel.dataset.hotspotId) !== String(featureId);
      });
  }

  function injectHotspots(host, features, configs) {
    const viewer = getViewer(host);
    if (!viewer || viewer.dataset.product3dHotspotsReady === 'true') return viewer;

    viewer.dataset.product3dHotspotsReady = 'true';
    viewer.style.setProperty('--min-hotspot-opacity', '0');

    features.forEach((feature, index) => {
      const hotspot = document.createElement('button');
      hotspot.type = 'button';
      hotspot.className = 'product-3d-hotspot';
      hotspot.slot = `hotspot-${index}`;
      hotspot.dataset.featureId = feature.id;
      hotspot.dataset.position = modelCoordinate(feature.position);

      const normal = normalCoordinate(feature.normal);
      if (normal) {
        hotspot.dataset.normal = normal;
        hotspot.dataset.visibilityAttribute = 'visible';
      }

      hotspot.setAttribute('aria-label', feature.title || `Product feature ${index + 1}`);
      hotspot.innerHTML = `<span aria-hidden="true">${index + 1}</span>`;
      hotspot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        activateFeature(host.dataset.productId, feature.id, viewer, configs);
      });
      viewer.appendChild(hotspot);
    });

    setupCalibration(host.closest('[data-product-3d-hotspots]'), viewer);

    return viewer;
  }

  function waitForViewer(host, timeout = 5000) {
    const existingViewer = getViewer(host);
    if (existingViewer) return Promise.resolve(existingViewer);

    return new Promise((resolve) => {
      let settled = false;
      const load = () => {
        if (!host?.hasAttribute('loaded')) {
          if (typeof host.loadContent === 'function') {
            host.loadContent(false);
          } else {
            host.querySelector('.deferred-media__poster')?.click();
          }
        }
      };
      const observer = new MutationObserver(() => {
        const viewer = getViewer(host);
        if (viewer && !settled) {
          settled = true;
          observer.disconnect();
          resolve(viewer);
        }
      });

      observer.observe(host, { childList: true, subtree: true });
      load();
      if (window.customElements?.whenDefined) {
        window.customElements.whenDefined('product-model').then(load);
      }

      window.setTimeout(() => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        resolve(getViewer(host));
      }, timeout);
    });
  }

  function observeHost(host, features, configs) {
    const inject = () => injectHotspots(host, features, configs);
    if (inject()) return;

    if (host.dataset.product3dHotspotsAutoload === 'true') {
      const load = () => {
        if (!host.hasAttribute('loaded')) {
          if (typeof host.loadContent === 'function') {
            host.loadContent(false);
          } else {
            host.querySelector('.deferred-media__poster')?.click();
          }
        }
      };
      load();
      if (window.customElements?.whenDefined) {
        window.customElements.whenDefined('product-model').then(load);
      }
    }

    const observer = new MutationObserver(() => {
      if (inject()) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });
  }

  function wireTrigger(trigger, configs) {
    if (trigger.dataset.product3dHotspotReady === 'true') return;
    trigger.dataset.product3dHotspotReady = 'true';

    trigger.addEventListener('click', () => {
      const productId = trigger.dataset.productId;
      const features = configs.get(String(productId)) || [];
      const hosts = getHosts(productId);
      const preferredHost = hosts.find((host) => host.dataset.product3dHotspotsAutoload === 'true') || hosts[0];

      activateFeature(productId, trigger.dataset.hotspotId, null, configs);
      waitForViewer(preferredHost).then((viewer) => {
        if (!viewer) return;
        injectHotspots(preferredHost, features, configs);
        activateFeature(productId, trigger.dataset.hotspotId, viewer, configs);
      });
    });
  }

  function setupFeatureNavigation(section) {
    if (!section || section.dataset.product3dHotspotsNavigationReady === 'true') return;

    const triggers = Array.from(section.querySelectorAll('[data-product-3d-hotspot-trigger]'));
    const previousButton = section.querySelector('[data-product-3d-hotspot-previous]');
    const nextButton = section.querySelector('[data-product-3d-hotspot-next]');
    if (!triggers.length || !previousButton || !nextButton) return;

    section.dataset.product3dHotspotsNavigationReady = 'true';

    function navigate(direction) {
      const activeIndex = triggers.findIndex((trigger) => trigger.classList.contains('is-active'));
      const currentIndex = activeIndex === -1 ? 0 : activeIndex;
      const nextIndex = (currentIndex + direction + triggers.length) % triggers.length;
      triggers[nextIndex].click();
    }

    previousButton.addEventListener('click', () => navigate(-1));
    nextButton.addEventListener('click', () => navigate(1));
  }

  function setup() {
    const configs = readConfigs();
    if (!configs.size) return;

    if (new URLSearchParams(window.location.search).get('hotspot_calibrate') === '1') {
      document.querySelectorAll('[data-product-3d-hotspots]').forEach((section) => {
        section.dataset.product3dHotspotsCalibration = 'true';
      });
    }

    configs.forEach((features, productId) => {
      getHosts(productId).forEach((host) => observeHost(host, features, configs));
    });

    document
      .querySelectorAll('[data-product-3d-hotspot-trigger]')
      .forEach((trigger) => wireTrigger(trigger, configs));

    document
      .querySelectorAll('[data-product-3d-hotspots]')
      .forEach(setupFeatureNavigation);

    // Give the section a considered starting state so the first feature is
    // already explained when the viewer appears, while retaining full
    // keyboard and click control for the rest of the list.
    document.querySelectorAll('[data-product-3d-hotspots]').forEach((section) => {
      if (section.dataset.product3dHotspotsInitialised === 'true') return;
      section.dataset.product3dHotspotsInitialised = 'true';

      const firstTrigger = section.querySelector('[data-product-3d-hotspot-trigger]');
      if (!firstTrigger) return;

      const productId = firstTrigger.dataset.productId;
      const features = configs.get(String(productId)) || [];
      const hosts = getHosts(productId);
      const preferredHost = hosts.find((host) => host.dataset.product3dHotspotsAutoload === 'true') || hosts[0];

      activateFeature(productId, firstTrigger.dataset.hotspotId, null, configs, { setCamera: false });
      waitForViewer(preferredHost).then((viewer) => {
        if (!viewer) return;
        injectHotspots(preferredHost, features, configs);
        setViewerDefaultCamera(viewer, features[0]);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup, { once: true });
  } else {
    setup();
  }

  document.addEventListener('shopify:section:load', setup);
})();
